import React, { useState } from 'react';
import { Award, CheckCircle, ChevronRight, BookOpen, Play, Info } from 'lucide-react';
import { AnimatePresence, motion } from 'framer-motion';
import VideoPlayer from './VideoPlayer'; // Re-imported here for the Hype Reel

/**
 * ModuleHub
 * Updated with "Hype Reel" logic (Update 7).
 */
const ModuleHub = ({ content = [], progress, progressPercent, onSelectPart, isReviewMode }) => {
  const [showHypeReel, setShowHypeReel] = useState(false);

  return (
    <div className="animate-fade-in h-full flex flex-col relative">
      
      {/* 1. THE HYPE REEL (Cinematic Header) */}
      <div className="mb-8 relative overflow-hidden rounded-2xl bg-slate-900 shadow-xl border border-slate-700 group">
         <div className="absolute inset-0 opacity-30 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')]"></div>
         <div className="absolute inset-0 bg-gradient-to-r from-blue-900/90 to-slate-900/90"></div>
         
         <div className="relative z-10 p-6 sm:p-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
            <div>
               <div className="flex items-center gap-2 text-blue-300 font-bold text-xs uppercase tracking-widest mb-2">
                  <Info size={14} /> Mission Briefing
               </div>
               <h2 className="text-2xl sm:text-3xl font-black text-white mb-2 leading-tight">
                  Why You Are Here
               </h2>
               <p className="text-slate-300 text-sm max-w-md leading-relaxed">
                  The "Soft Skills Gap" is why 78% of qualified candidates get rejected. Watch the intel briefing to understand the battlefield.
               </p>
            </div>
            
            <button 
               onClick={() => setShowHypeReel(true)}
               className="flex-shrink-0 w-16 h-16 rounded-full bg-white/10 hover:bg-white/20 backdrop-blur border border-white/20 flex items-center justify-center text-white transition-all transform hover:scale-110 shadow-[0_0_20px_rgba(59,130,246,0.5)] group-hover:shadow-[0_0_40px_rgba(59,130,246,0.8)]"
            >
               <Play fill="currentColor" className="ml-1" size={24} />
            </button>
         </div>
      </div>

      {/* Progress Section */}
      <div className="flex items-center justify-between mb-4">
         <h3 className="font-bold text-gray-800 text-lg">Active Operations</h3>
         <span className="text-xs font-bold text-gray-500 bg-gray-100 px-2 py-1 rounded-full">{progressPercent.toFixed(0)}% Ready</span>
      </div>

      {/* Dynamic Lesson List */}
      <div className="space-y-3 flex-grow overflow-y-auto pb-4">
        {content.map(part => {
          const isCompleted = progress[part.id];
          
          return (
            <button
              key={part.id}
              onClick={() => onSelectPart(part.id)}
              className={`
                w-full text-left p-4 rounded-xl border-2 transition-all duration-200 group cursor-pointer
                flex items-center justify-between
                ${isCompleted 
                  ? 'bg-white border-green-200 hover:border-green-400 hover:shadow-md' 
                  : 'bg-white border-slate-100 hover:border-blue-400 hover:shadow-lg'
                }
              `}
            >
              <div className="flex items-center">
                {/* Icon State */}
                <div className={`
                  w-12 h-12 rounded-xl flex items-center justify-center mr-4 transition-colors shadow-sm
                  ${isCompleted ? 'bg-green-100 text-green-600' : 'bg-slate-50 text-slate-400 group-hover:bg-blue-600 group-hover:text-white'}
                `}>
                  {isCompleted ? <CheckCircle className="w-6 h-6" /> : <part.icon className="w-6 h-6" />}
                </div>

                {/* Text Content */}
                <div>
                  <h3 className={`font-black text-sm sm:text-base ${isCompleted ? 'text-gray-800' : 'text-slate-700'}`}>
                    {part.title}
                  </h3>
                  <p className="text-xs text-gray-500 mt-0.5 line-clamp-1">{part.description}</p>
                </div>
              </div>

              <div className="text-slate-300 group-hover:text-blue-500 transition-colors">
                {isReviewMode ? (
                    <BookOpen className="w-5 h-5" /> 
                ) : (
                    <ChevronRight className="w-6 h-6" />
                )}
              </div>
            </button>
          );
        })}
      </div>

      {/* HYPE REEL MODAL */}
      <AnimatePresence>
         {showHypeReel && (
            <motion.div 
               initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
               className="fixed inset-0 z-50 bg-black/90 backdrop-blur-md flex flex-col items-center justify-center p-4"
               onClick={() => setShowHypeReel(false)}
            >
               <div className="w-full max-w-4xl" onClick={e => e.stopPropagation()}>
                  <VideoPlayer 
                     videoText="CINEMATIC INTRO: The Hidden Rules of the Workplace"
                     videoUrl="https://www.youtube.com/watch?v=dQw4w9WgXcQ" // Placeholder
                     videoIndex={0}
                     totalVideos={1}
                     onNext={() => setShowHypeReel(false)}
                     onPrev={() => setShowHypeReel(false)}
                  />
               </div>
               <button className="mt-8 text-white/50 hover:text-white font-bold uppercase tracking-widest text-xs flex items-center gap-2">
                  Tap anywhere to close
               </button>
            </motion.div>
         )}
      </AnimatePresence>

    </div>
  );
};

export default ModuleHub;